import { NoddiButton, NoddiInput } from 'noddi-ui-web';
import { useState } from 'react';
import { isProduction } from '../../constants/environment';
import { useApiBaseUrlOverride, useSetApiBaseUrlOverride } from '../../stores/DevSettingsStore';

// Lets non-production builds (e.g. Netlify deploy previews) route this app at a
// different API — typically a local backend exposed via a Tailscale funnel. The
// override is persisted in the dev-settings localStorage and applied on the next
// full page load (see resolveApiBaseUrl in each app's index.tsx), which also clears
// the React Query cache so data never mixes across backends.
export const ApiBaseUrlOverrideCard = () => {
  const override = useApiBaseUrlOverride();
  const setOverride = useSetApiBaseUrlOverride();
  const [value, setValue] = useState(override ?? '');
  const [isProbing, setIsProbing] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);

  if (isProduction) {
    return null;
  }

  const trimmed = value.trim();
  const buildTimeUrl = import.meta.env.VITE_APP_BE_URL ?? '';
  const isDirty = (override ?? '') !== trimmed;
  const isValidUrl = isHttpUrl(trimmed);

  // Reset to default is unconditional; saving an override is gated behind a
  // reachability probe so a valid-but-dead URL can't strand the app.
  const apply = (next: string | null) => {
    setOverride(next);
    window.location.reload();
  };

  const save = async () => {
    setProbeError(null);
    setIsProbing(true);
    const reachable = await isReachable(trimmed);
    if (!reachable) {
      setIsProbing(false);
      setProbeError("Couldn't reach that URL. Check the backend is up before saving.");
      return;
    }
    apply(trimmed);
  };

  return (
    <div className='flex flex-col gap-3 rounded-lg border p-4'>
      <div className='flex flex-col gap-1'>
        <span className='text-sm font-medium'>Backend URL override</span>
        <span className='text-xs text-muted-foreground'>
          Route this app at a different API (e.g. a local backend via Tailscale funnel). Applied on reload. Build
          default: <span className='font-mono break-all'>{buildTimeUrl || '(none)'}</span>
        </span>
        <span className='text-xs text-muted-foreground'>
          Save is gated behind a reachability check, but a URL that dies later (e.g. machine sleeps) persists across
          reloads — reset it here to recover. If this menu is ever unreachable, clear the{' '}
          <span className='font-mono'>dev-settings</span> key in localStorage.
        </span>
      </div>
      <NoddiInput
        name='api-base-url-override'
        placeholder='https://my-machine.tailnet.ts.net/'
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setProbeError(null);
        }}
      />
      {trimmed && !isValidUrl && (
        <span className='text-xs font-medium text-destructive'>Enter a valid http(s) URL.</span>
      )}
      {probeError && <span className='text-xs font-medium text-destructive'>{probeError}</span>}
      <div className='flex gap-2'>
        <NoddiButton
          size='small'
          loading={isProbing}
          disabled={!trimmed || !isValidUrl || !isDirty || isProbing}
          // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs save(); React discards the returned promise, so a rejection is unhandled unless the body catches
          onClick={save}
        >
          Save & reload
        </NoddiButton>
        <NoddiButton size='small' variant='outline' disabled={!override || isProbing} onClick={() => apply(null)}>
          Reset to default
        </NoddiButton>
      </div>
      {override && (
        <span className='text-xs font-medium break-all'>
          Active override: <span className='font-mono'>{override}</span>
        </span>
      )}
    </div>
  );
};

const isHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

// A no-cors probe resolves on any HTTP response (even an opaque 4xx/5xx) and
// rejects only on a real network failure — DNS miss, refused connection, or the
// 4s timeout. That's exactly the "is the host up?" signal we want, and it
// sidesteps CORS so we don't depend on the backend allowing this origin for a
// preflight.
const isReachable = async (url: string): Promise<boolean> => {
  try {
    await fetch(url, { method: 'HEAD', mode: 'no-cors', signal: AbortSignal.timeout(4000) });
    return true;
  } catch {
    return false;
  }
};
